"use client";

import { formatInTimeZone } from "date-fns-tz";

import { cn } from "@/lib/utils";

import { useNow } from "../../hooks/use-now";
import { WidgetEmpty } from "../widget-card";
import { SevenSegmentTime } from "./clock-faces";

import type { ClockItem, ClocksPayload } from "@/types";

/**
 * A row of live clocks.
 *
 * The only payload on the dashboard that keeps moving after it lands. Everything
 * else here draws a number the server computed; this draws a number that is wrong
 * a second later, so the server sends the parts that DON'T change — the city and
 * its offset — and the card does the rest.
 *
 * Which is also why the time is computed from `timezone` and not from
 * `utc_offset_minutes`, even though the offset is right there. An offset is only
 * true for an instant: a dashboard left open across a daylight-saving change
 * would tick an hour wrong for the rest of the session. The IANA identifier
 * doesn't go stale, and `date-fns-tz` resolves it against the browser's own copy
 * of the same database PHP used. The offset is what we PRINT — "UTC+3", the thing
 * you actually want to know about a city — not what we compute from.
 */
export function ClocksWidget({ payload }: { payload: ClocksPayload }) {
  // One timer for the row: every clock reads the same instant, so four timers
  // would cost four times as much and could still disagree by a second.
  const now = useNow(1000);

  if (payload.empty_text || payload.items.length === 0) {
    return <WidgetEmpty label={payload.empty_text ?? ""} />;
  }

  return (
    // Two up on a phone, then one column per clock. Sized to the number actually
    // shown rather than a fixed four: with two cities chosen, two half-width
    // tiles look deliberate while two quarter-width ones look like something
    // failed to load.
    <div
      className={cn(
        "grid flex-1 grid-cols-2 gap-3",
        payload.items.length === 3 && "sm:grid-cols-3",
        payload.items.length >= 4 && "sm:grid-cols-4",
      )}
    >
      {payload.items.map((item) => (
        <Clock key={item.timezone} item={item} now={now} />
      ))}
    </div>
  );
}

function Clock({ item, now }: { item: ClockItem; now: Date }) {
  // 24-hour throughout: this card exists to answer "is it a reasonable hour to
  // call them", and am/pm is one more thing to decode at a glance.
  const time = formatInTimeZone(now, item.timezone, "HH:mm");

  return (
    // The DS's raised tile, the same one the list widget's rows use, so a clock
    // reads as an object on the card rather than a floating figure. `raised`,
    // not the card shadow — that one's 10x14 offset is drawn for a whole card
    // and under a tile this size it reads as a slab rather than a lift.
    <div className="flex flex-col items-center gap-2 rounded-md bg-surface-input px-2 py-4 shadow-raised">
      {/* The digits are decorative SVG, so the tile carries the accessible name
          for the whole clock. Read as "Paris 14:02, UTC+2". */}
      <div role="img" aria-label={`${item.city} ${time}, ${item.utc_offset_label}`}>
        <SevenSegmentTime time={time} />
      </div>

      <div className="flex min-w-0 flex-col items-center gap-0.5 text-center">
        <span className="max-w-full truncate font-display text-sm text-foreground">
          {item.city}
        </span>
        {/* The offset the backend resolved. Under the city because it qualifies
            it — "Paris, two hours ahead of UTC" — and it is the half of the
            answer a bare time doesn't give you. */}
        <span className="font-display text-xs text-fg-muted tabular-nums">
          {item.utc_offset_label}
        </span>
      </div>
    </div>
  );
}
